import { checkAuthAndLogout } from "../utils/auth";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://whatzproapi.theonetechnologies.co.in/api";

// When using ngrok for local testing, skip the browser warning interstitial
const isNgrok = API_BASE_URL.includes('ngrok');
export const DEFAULT_HEADERS: Record<string, string> = {
  "Content-Type": "application/json",
  ...(isNgrok ? { "ngrok-skip-browser-warning": "true" } : {}),
};

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  message?: string;
  code: number;
  data: {
    user: {
      _id: string;
      name: string;
      email: string;
      phone: string;
      createdAt: string;
      token: string;
      updatedAt: string;
    };
  };
}

interface RegisterData {
  name: string;
  email: string;
  phone: string;
  password: string;
}

interface ForgotPasswordData {
  email: string;
}

interface ForgotPasswordResponse {
  success: boolean;
  message?: string;
  code: number;
}

interface ResetPasswordData {
  token: string;
  password: string;
  confirmPassword: string;
}

interface ResetPasswordResponse {
  success: boolean;
  message?: string;
  code: number;
}

interface ProfileResponse {
  success: boolean;
  message?: string;
  code: number;
  data: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface Invoice {
  id: string;
  subscription_id: string;
  status: string;
  line_items: LineItem[];
  date: number;
  invoice_number: string | null;
  customer_details: {
    customer_name: string | null;
    customer_email: string | null;
    customer_contact: string | null;
  };
  currency: string;
  currency_symbol: string;
  gross_amount: number;
  tax_amount: number;
  amount: number;
  amount_paid: number;
  amount_due: number;
  issued_at: number;
  paid_at: number | null;
  billing_start: number | null;
  billing_end: number | null;
}

export interface LineItem {
  id: string;
  name: string;
  description: string | null;
  amount: number;
  unit_amount: number;
  quantity: number;
}

interface InvoiceResponse {
  success: boolean;
  message?: string;
  invoices: Invoice[];
}

interface GHLAuthResponse {
  success: boolean;
  message?: string;
  code: number;
  data: string;
}

interface Subscription {
  _id: string;
  userId: string;
  locationId: string;
  locationName: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface SubscriptionResponse {
  success: boolean;
  message?: string;
  code: number;
  data: {
    subscriptions: Subscription[];
  };
}

interface SubAccount {
  locationId: string;
  locationName: string;
  _id: string;
}

interface SubscribedLocation {
  _id: string;
  userId: string;
  razorpaySubscriptionId: string;
  planId: string;
  planName: string;
  trialEndsAt: string;
  subscriptionCreatedDate: string;
  subscriptionEndDate: string;
  razorpayShortUrl: string;
  status: string;
  subAccounts: SubAccount[];
}

interface SubscribedLocationsResponse {
  success: boolean;
  subscriptions: SubscribedLocation[];
  message?: string;
}

interface CreateSubscriptionData {
  planId: string;
}

interface CreateSubscriptionResponse {
  success: boolean;
  message?: string;
  code?: number;
  data?: {
    subscriptionId: string;
    status: string;
  };
}

export interface Plan {
  id: string;
  entity: string;
  interval: number;
  period: string;
  item: {
    id: string;
    active: boolean;
    name: string;
    description: string;
    amount: number;
    unit_amount: number;
    currency: string;
    type: string;
    unit: string | null;
    tax_inclusive: boolean;
    hsn_code: string | null;
    sac_code: string | null;
    tax_rate: number | null;
    tax_id: string | null;
    tax_group_id: string | null;
    created_at: number;
    updated_at: number;
  };
  notes: any[];
  created_at: number;
}

interface GetPlansResponse {
  success: boolean;
  message?: string;
  data: Plan[];
}

interface GetSubscriptionStatusResponse {
  success: boolean;
  message?: string;
  data: {
    status: string;
  };
}

interface OTPRequestData {
  userId: string;
  email: string;
}

interface OTPVerifyData extends OTPRequestData {
  otp: number;
}

interface OTPResponse {
  success: boolean;
  message?: string;
  code: number;
}

const handleApiResponse = async (response: Response) => {
  const data = await response.json();

  if (response.status === 401) {
    // Token is invalid or expired
    checkAuthAndLogout();
    throw new Error("Session expired. Please login again.");
  }

  if (!response.ok) {
    throw new Error(data.message || "An error occurred");
  }

  return data;
};

export const loginUser = async (credentials: LoginCredentials) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/login`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify(credentials),
    });

    const data = await handleApiResponse(response);

    return {
      user: data.data.user,
      token: data.data.user.token,
    };
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const registerUser = async (userData: RegisterData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/register`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Registration failed");
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const forgotPassword = async (data: ForgotPasswordData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/forgot-password`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify(data),
    });

    const responseData: ForgotPasswordResponse = await response.json();

    if (!response.ok) {
      throw new Error(
        responseData.message || "Failed to process forgot password request"
      );
    }

    if (!responseData.success) {
      throw new Error(
        responseData.message || "Failed to process forgot password request"
      );
    }

    return responseData;
  } catch (error) {
    console.error("Forgot Password API Error:", error);
    throw error;
  }
};

export const resetPassword = async (data: ResetPasswordData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/reset-password`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify(data),
    });

    const responseData: ResetPasswordResponse = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || "Failed to reset password");
    }

    if (!responseData.success) {
      throw new Error(responseData.message || "Failed to reset password");
    }

    return responseData;
  } catch (error) {
    console.error("Reset Password API Error:", error);
    throw error;
  }
};

export const getProfile = async (): Promise<ProfileResponse> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/users/profile`, {
      method: "GET",
      headers: {
        ...DEFAULT_HEADERS,
        Authorization: `Bearer ${token}`,
      },
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const getInvoices = async (
  startDate?: string,
  endDate?: string
): Promise<{
  data: Invoice[];
  message: string;
}> => {
  let url = `${API_BASE_URL}/subscription/invoices`;

  if (startDate && endDate) {
    url += `?startDate=${encodeURIComponent(
      startDate
    )}&endDate=${encodeURIComponent(endDate)}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || "Failed to fetch invoices");
  }

  const result = await response.json();

  // Validate format
  if (!Array.isArray(result.data)) {
    throw new Error(
      "Unexpected response format. Invoice data is not an array."
    );
  }

  return { data: result.data, message: result.message };
};

export const initiateGHLAuth = async (
  locationId: string
): Promise<GHLAuthResponse> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await fetch(
      `${API_BASE_URL}/auth/ghl?location_id=${locationId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data: GHLAuthResponse = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to authenticate with GHL");
    }

    if (!data.success) {
      throw new Error(data.message || "Failed to authenticate with GHL");
    }

    return data;
  } catch (error) {
    console.error("GHL Auth API Error:", error);
    throw error;
  }
};

export const getSubscriptions = async (): Promise<Subscription[]> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/subscription/get`, {
      method: "GET",
      headers: {
        ...DEFAULT_HEADERS,
        Authorization: token,
      },
    });

    const data: SubscriptionResponse = await handleApiResponse(response);
    return data.data.subscriptions;
  } catch (error) {
    console.error("Get Subscriptions API Error:", error);
    throw error;
  }
};

export const getSubscribedLocations = async (): Promise<
  SubscribedLocationsResponse & { message?: string }
> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No authentication token found");

    const response = await fetch(`${API_BASE_URL}/subscription/get`, {
      method: "GET",
      headers: {
        ...DEFAULT_HEADERS,
        Authorization: token,
      },
    });

    // 404 means no subscriptions yet — return empty list instead of throwing
    if (response.status === 404) {
      return { success: true, subscriptions: [] };
    }

    const result = await handleApiResponse(response);
    if (!Array.isArray(result.data)) {
      throw new Error("Unexpected response format: data is not an array");
    }

    return { ...result, subscriptions: result.data };
  } catch (error) {
    console.error("Get Subscribed Locations API Error:", error);
    throw error;
  }
};

export const ghlCallback = async (code: string): Promise<void> => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Authentication token not found");
  }

  const locationId = localStorage.getItem("tempLocationId");
  const params = new URLSearchParams({ code });
  if (locationId) {
    params.set("location_id", locationId);
  }

  const response = await fetch(
    `${API_BASE_URL}/auth/callback?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || "Failed to complete GHL callback");
  }

  localStorage.removeItem("tempLocationId");
  localStorage.removeItem("ghlRedirectUrl");
};

export const createSubscription = async (
  subscriptionData: CreateSubscriptionData
): Promise<CreateSubscriptionResponse> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await fetch(`${API_BASE_URL}/subscription/create`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
        Authorization: token,
      },
      body: JSON.stringify(subscriptionData),
    });

    // Ensure response is ok (status code 200-299)
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to create subscription");
    }

    // Parse and return the response as CreateSubscriptionResponse
    const data: CreateSubscriptionResponse = await response.json();

    // Handle API response success
    if (!data.success) {
      throw new Error(data.message || "Failed to create subscription");
    }

    return data;
  } catch (error) {
    console.error("Create Subscription API Error:", error);
    // Handle and re-throw the error as needed
    throw error;
  }
};

export const getSubscriptionPlans = async (): Promise<GetPlansResponse> => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Authentication token not found");

  const response = await fetch(`${API_BASE_URL}/subscription/plans`, {
    method: "GET",
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: token,
    },
  });

  const data: GetPlansResponse = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch subscription plans");
  }

  return data;
};

export const getSubscriptionStatus =
  async (): Promise<GetSubscriptionStatusResponse> => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const response = await fetch(`${API_BASE_URL}/subscription/get`, {
        method: "GET",
        headers: {
          ...DEFAULT_HEADERS,
          Authorization: token,
        },
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error("Get Subscription Status API Error:", error);
      throw error;
    }
  };

/**
 * Cancel a subscription
 * @param razorpaySubscriptionId string
 * @param cancelAtCycleEnd boolean
 * @returns Promise<any>
 */
export const cancelSubscription = async (
  razorpaySubscriptionId: string,
  cancelAtCycleEnd: boolean
): Promise<any> => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Authentication token not found");

  const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
    method: "POST",
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: token,
    },
    body: JSON.stringify({ razorpaySubscriptionId, cancelAtCycleEnd }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to cancel subscription");
  }

  return await response.json();
};

export const createTestSubscription = async (): Promise<{ success: boolean; message?: string; data?: any }> => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Authentication token not found");

  const response = await fetch(`${API_BASE_URL}/subscription/create-test`, {
    method: "POST",
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: `Bearer ${token}`,
    },
  });

  return await handleApiResponse(response);
};

export const sendOTP = async (data: OTPRequestData): Promise<OTPResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/otp/send`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify(data),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Send OTP API Error:", error);
    throw error;
  }
};

export const resendOTP = async (data: OTPRequestData): Promise<OTPResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/otp/resend`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify(data),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Resend OTP API Error:", error);
    throw error;
  }
};

export const verifyOTP = async (data: OTPVerifyData): Promise<OTPResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/otp/verify`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify(data),
    });

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Verify OTP API Error:", error);
    throw error;
  }
};

export const googleAuth = async (credential: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: "POST",
      headers: {
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify({ token: credential }),
    });

    if (!response.ok) {
      throw new Error("Google authentication failed");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Google auth error:", error);
    throw error;
  }
};
